import { useForm } from '@tanstack/react-form';
import { isFormField } from '@tronche2tek/shared-types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CardPhase, PreflightInput } from '../../../api/cards';
import {
  cardMessageText,
  commitCards,
  getCardTemplateSvgs,
  getPreflightRecap,
  postPreflight,
} from '../../../api/cards';
import type { SseHandle } from '../../../api/cards-sse';
import { openCardStream } from '../../../api/cards-sse';
import type { CardResult } from '../../../types';
import { extractExtraFields, initialSingleCardFields } from './single-card';

/**
 * The single-card form. Wrapped so the hooks below can name its exact type —
 * `setFieldValue` is typed against the field paths inferred from the initial
 * values, so a looser signature will not do.
 */
export function useSingleCardForm(onSubmit: () => void) {
  return useForm({
    defaultValues: initialSingleCardFields(),
    onSubmit: () => onSubmit(),
  });
}

export type SingleCardForm = ReturnType<typeof useSingleCardForm>;

/**
 * The template chosen for a single card, and everything derived from its SVGs.
 * Kept out of the form because none of it is typed by the operator: it is what
 * the chosen template turns out to contain.
 */
export function useTemplatePick(form: SingleCardForm) {
  const [resolvedSvg, setResolvedSvg] = useState<string | null>(null);
  // Liquid vars per side, kept separate so a single-side generation only
  // surfaces (and requires) the fields that side actually uses.
  const [frontExtras, setFrontExtras] = useState<string[]>([]);
  const [backExtras, setBackExtras] = useState<string[]>([]);
  const [defaults, setDefaults] = useState<Record<string, string>>({});

  const clear = useCallback(() => {
    form.setFieldValue('templateId', '');
    form.setFieldValue('customTemplateFile', null);
    form.setFieldValue('fieldValues', {});
    form.setFieldValue('sides', 'front');
    setResolvedSvg(null);
    setFrontExtras([]);
    setBackExtras([]);
    setDefaults({});
  }, [form]);

  /**
   * Seed the editable extra-field inputs from a template's Liquid vars and
   * defaults. Called from the select/file handlers — a user action, so no
   * effect is needed. A `form:` default seeds an empty input, since its value
   * is filled here rather than stored.
   */
  const apply = useCallback(
    (
      svg: string,
      backSvg?: string | null,
      tplDefaults: Record<string, string> = {},
    ) => {
      const extras = extractExtraFields(svg, tplDefaults);
      const back = backSvg ? extractExtraFields(backSvg, tplDefaults) : [];
      setResolvedSvg(svg);
      setFrontExtras(extras);
      setBackExtras(back);
      setDefaults(tplDefaults);
      form.setFieldValue(
        'fieldValues',
        Object.fromEntries(
          [...new Set([...extras, ...back])].map((f) => [
            f,
            isFormField(tplDefaults[f]) ? '' : (tplDefaults[f] ?? ''),
          ]),
        ),
      );
    },
    [form],
  );

  const selectSaved = useCallback(
    async (id: string) => {
      clear();
      form.setFieldValue('templateId', id);
      if (!id) {
        return;
      }
      try {
        const {
          frontSvg,
          backSvg,
          defaults: stored,
        } = await getCardTemplateSvgs(id);
        apply(frontSvg, backSvg, stored);
      } catch {
        /* noop — the picker simply stays empty */
      }
    },
    [apply, clear, form],
  );

  const pickFile = useCallback(
    async (file: File) => {
      clear();
      form.setFieldValue('customTemplateFile', file);
      apply(await file.text(), null);
    },
    [apply, clear, form],
  );

  // One stable object per change: SingleCardGenerator keys memos on this.
  return useMemo(
    () => ({
      resolvedSvg,
      frontExtras,
      backExtras,
      defaults,
      clear,
      selectSaved,
      pickFile,
    }),
    [
      resolvedSvg,
      frontExtras,
      backExtras,
      defaults,
      clear,
      selectSaved,
      pickFile,
    ],
  );
}

/**
 * One card's generation run: pre-flight, auto-commit, render, and the SSE
 * stream behind all three. There is no interactive recap for a single
 * recipient — if it passed pre-flight the render starts on its own, and if it
 * did not, the form is cleared and the reason surfaced.
 */
export function useSingleCardRun(onFailure: () => void) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<CardPhase>('idle');
  const [result, setResult] = useState<CardResult | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const esRef = useRef<SseHandle | null>(null);

  const closeStream = () => {
    esRef.current?.close();
    esRef.current = null;
  };

  /** Back to a clean slate, keeping whatever the operator had picked. */
  const reset = useCallback(() => {
    closeStream();
    setPhase('idle');
    setResult(null);
    setJobId(null);
    setError(null);
  }, []);

  // On failure the form goes back to empty and the message sits under it, so
  // the operator starts over rather than retrying a broken selection.
  const failWith = useCallback(
    (message: string) => {
      closeStream();
      onFailure();
      setResult(null);
      setJobId(null);
      setError(message);
      setPhase('error');
    },
    [onFailure],
  );

  const commitAndRender = useCallback(
    async (resolveBatchId: string) => {
      let recap;
      try {
        recap = await getPreflightRecap(resolveBatchId);
      } catch (err) {
        failWith(err instanceof Error ? err.message : String(err));
        return;
      }
      if (!recap.valid.length) {
        const first = recap.errors[0];
        failWith(
          (first && cardMessageText(t, first)) ||
            t('singleCardGenerator.failed'),
        );
        return;
      }

      let id: string;
      try {
        id = (await commitCards(resolveBatchId, 'pdf')).jobId;
      } catch (err) {
        failWith(err instanceof Error ? err.message : String(err));
        return;
      }
      setJobId(id);

      // Track the latest per-card result in the closure so the `done` handler
      // can read it synchronously (state would be stale here).
      let lastResult: CardResult | null = null;
      await openCardStream({
        path: `/cards/stream/${id}`,
        esRef,
        onProgress: (data) => {
          if (data.result) {
            lastResult = data.result;
            setResult(data.result);
          }
        },
        onDone: () => {
          if (lastResult && lastResult.status !== 'success') {
            failWith(
              cardMessageText(t, lastResult) || t('singleCardGenerator.failed'),
            );
            return;
          }
          setPhase('done');
        },
        onError: failWith,
      });
    },
    [failWith, t],
  );

  const start = useCallback(
    async (input: PreflightInput) => {
      setPhase('uploading');
      setResult(null);
      setError(null);

      let resolveBatchId: string;
      try {
        resolveBatchId = await postPreflight(input);
      } catch (err) {
        failWith(err instanceof Error ? err.message : String(err));
        return;
      }

      setPhase('processing');
      await openCardStream({
        path: `/cards/stream/${resolveBatchId}`,
        esRef,
        onDone: () => {
          void commitAndRender(resolveBatchId);
        },
        onError: failWith,
      });
    },
    [commitAndRender, failWith],
  );

  // Same identity rule as useTemplatePick above.
  return useMemo(
    () => ({ phase, result, jobId, error, start, reset }),
    [phase, result, jobId, error, start, reset],
  );
}
